import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Breadcrumbs,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  Link,
  MenuItem,
  Paper,
  Select,
  Stack,
  TablePagination,
  ToggleButton,
  ToggleButtonGroup,
  TextField,
  Typography,
  Snackbar,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import { DataGrid } from "@mui/x-data-grid";
import Visibility from "@mui/icons-material/Visibility";
import EditOutlined from "@mui/icons-material/EditOutlined";
import DeleteOutlined from "@mui/icons-material/DeleteOutlined";
import SearchIcon from "@mui/icons-material/Search";
import useAuth from "../../hooks/useAuth";
import { getTenantsList } from "../../api/tenant";
import { getTreatments, deleteTreatment } from "../../api/treatment";
import { APP_HOME_PATH } from "../../config/routes";
import useIsMobileList from "../../hooks/useIsMobileList";

const TREATMENT_CHIP = {
  date: { bgcolor: "#e3f2fd", color: "#1565c0", borderColor: "#90caf9" },
  condition: { bgcolor: "#fff3e0", color: "#e65100", borderColor: "#ffcc80" },
  medicine: { bgcolor: "#e8f5e9", color: "#2e7d32", borderColor: "#a5d6a7" },
  cost: { bgcolor: "#fce4ec", color: "#c62828", borderColor: "#ef9a9a" },
  group: { bgcolor: "#f3e5f5", color: "#7b1fa2", borderColor: "#ce93d8" },
};

const TREATMENT_SCOPE = {
  individual: {
    label: "Individual",
    accent: "#1565c0",
    chipBg: "#1565c0",
    bgcolor: "#e3f2fd",
    borderColor: "#90caf9",
  },
  group: {
    label: "Group",
    accent: "#7b1fa2",
    chipBg: "#7b1fa2",
    bgcolor: "#f3e5f5",
    borderColor: "#ce93d8",
  },
  default: {
    label: "Treatment",
    accent: "#2e7d32",
    chipBg: "#2e7d32",
    bgcolor: "#f1f8e9",
    borderColor: "#a5d6a7",
  },
};

function getTreatmentScopeStyle(scope) {
  return TREATMENT_SCOPE[scope] || TREATMENT_SCOPE.default;
}

function TreatmentMetaChip({ label, column }) {
  const style = TREATMENT_CHIP[column];
  return (
    <Chip
      size="small"
      label={label}
      component="span"
      clickable={false}
      sx={{
        height: 24,
        fontSize: "0.7rem",
        fontWeight: 600,
        bgcolor: style.bgcolor,
        color: style.color,
        border: "1px solid",
        borderColor: style.borderColor,
        pointerEvents: "none",
        cursor: "default",
        "& .MuiChip-label": { px: 1 },
      }}
    />
  );
}

function TreatmentScopeChip({ scope }) {
  const style = getTreatmentScopeStyle(scope);
  return (
    <Chip
      size="small"
      label={style.label}
      component="span"
      clickable={false}
      sx={{
        height: 22,
        fontWeight: 700,
        fontSize: "0.7rem",
        flexShrink: 0,
        bgcolor: style.chipBg,
        color: "#fff",
        pointerEvents: "none",
        cursor: "default",
        "& .MuiChip-label": { px: 1 },
      }}
    />
  );
}

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    const [y, m, d] = String(iso).slice(0, 10).split("-").map(Number);
    const dt = new Date(y, (m || 1) - 1, d || 1);
    return dt.toLocaleDateString(undefined, {
      month: "short",
      day: "2-digit",
      year: "numeric",
    });
  } catch {
    return iso;
  }
};

const formatMoney = (value, fractionDigits = 2) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return "—";
  return new Intl.NumberFormat(undefined, {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(num);
};

const formatUgx = (value, fractionDigits = 2) => {
  const formatted = formatMoney(value, fractionDigits);
  return formatted === "—" ? "—" : `ugx ${formatted}`;
};

function TreatmentCard({ row, profileQuery, onDelete }) {
  const scopeStyle = getTreatmentScopeStyle(row.treatment_scope);
  const subject =
    row.treatment_scope === "group"
      ? row.group_name || "unnamed group"
      : row.animal_tag_number || row.animal_id || "—";
  const costLabel =
    row.dispensed_total_cost == null
      ? "—"
      : formatUgx(row.dispensed_total_cost, 2);

  return (
    <Paper
      elevation={0}
      sx={{
        px: 1.5,
        py: 1.25,
        borderRadius: 2,
        border: "1px solid",
        borderColor: scopeStyle.borderColor,
        bgcolor: scopeStyle.bgcolor,
        borderLeft: `4px solid ${scopeStyle.accent}`,
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
        spacing={1}
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            variant="subtitle2"
            fontWeight={700}
            color="primary.main"
            noWrap
          >
            {row.treatment_id || `Treatment #${row.id}`}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            display="block"
            noWrap
          >
            {row.treatment_scope === "group" ? `Group: ${subject}` : subject}
          </Typography>
        </Box>
        <TreatmentScopeChip scope={row.treatment_scope} />
      </Stack>

      <Stack
        direction="row"
        flexWrap="wrap"
        useFlexGap
        spacing={0.5}
        sx={{ mt: 0.75, rowGap: 0.5 }}
      >
        <TreatmentMetaChip label={fmtDate(row.date)} column="date" />
        {row.treatment_of ? (
          <TreatmentMetaChip label={row.treatment_of} column="condition" />
        ) : null}
        {row.medicine ? (
          <TreatmentMetaChip label={row.medicine} column="medicine" />
        ) : null}
        {row.treatment_scope === "group" && row.group_size != null ? (
          <TreatmentMetaChip
            label={`${row.group_size} animals`}
            column="group"
          />
        ) : null}
        <TreatmentMetaChip label={costLabel} column="cost" />
      </Stack>

      <Stack
        direction="row"
        justifyContent="flex-end"
        spacing={0.5}
        alignItems="center"
        sx={{ mt: 1 }}
      >
        <Button
          size="small"
          variant="outlined"
          startIcon={<Visibility />}
          component={RouterLink}
          to={`/treatment/${row.id}${profileQuery}`}
          sx={{ fontWeight: 600 }}
        >
          View
        </Button>
        <IconButton
          size="small"
          color="primary"
          component={RouterLink}
          to={`/treatment/${row.id}/edit${profileQuery}`}
          aria-label="edit-treatment"
          sx={{
            border: "1px solid",
            borderColor: "primary.main",
            borderRadius: 1,
          }}
        >
          <EditOutlined fontSize="small" />
        </IconButton>
        <IconButton
          size="small"
          color="error"
          onClick={() => onDelete(row)}
          aria-label="delete-treatment"
          sx={{
            border: "1px solid",
            borderColor: "error.main",
            borderRadius: 1,
          }}
        >
          <DeleteOutlined fontSize="small" />
        </IconButton>
      </Stack>
    </Paper>
  );
}

export default function TreatmentList() {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));
  const isSm = useMediaQuery(theme.breakpoints.down("md"));
  const isMobile = useIsMobileList("md");
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const [tenants, setTenants] = useState([]);
  const [tenantFilter, setTenantFilter] = useState(null);

  const isSuperAdmin = user?.roles?.some(
    (r) => r === "super_admin" || r?.name === "super_admin"
  );

  const effectiveTenantId = isSuperAdmin
    ? tenantFilter?.id ?? null
    : user?.tenant_id ?? null;

  const farmTenantParam =
    isSuperAdmin && effectiveTenantId != null ? effectiveTenantId : null;

  const profileQuery =
    farmTenantParam != null ? `?tenant_id=${farmTenantParam}` : "";

  useEffect(() => {
    if (!isSuperAdmin) return;
    let cancelled = false;
    (async () => {
      try {
        const raw = await getTenantsList({ for_filter: true });
        if (!cancelled) setTenants(Array.isArray(raw) ? raw : []);
      } catch {
        if (!cancelled) setTenants([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isSuperAdmin]);

  useEffect(() => {
    if (!isSuperAdmin || tenants.length === 0 || tenantFilter != null) return;
    const raw = searchParams.get("tenant_id");
    if (!raw) return;
    const id = Number(raw);
    const t = tenants.find((x) => x.id === id);
    if (t) setTenantFilter(t);
  }, [isSuperAdmin, tenants, searchParams, tenantFilter]);

  const onOrgChange = (_, v) => {
    setTenantFilter(v);
    if (isSuperAdmin && v?.id != null) {
      setSearchParams({ tenant_id: String(v.id) }, { replace: true });
    }
  };

  const [rows, setRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [sortModel, setSortModel] = useState([{ field: "date", sort: "desc" }]);
  const [filterModel, setFilterModel] = useState({
    items: [],
    quickFilterValues: [],
  });
  const [scopeFilter, setScopeFilter] = useState("all");

  const [userColumnVisibility, setUserColumnVisibility] = useState({});
  const [cardSearchInput, setCardSearchInput] = useState("");
  const prevMobileRef = useRef(isMobile);
  const [snack, setSnack] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const closeSnack = () => setSnack((s) => ({ ...s, open: false }));

  useEffect(() => {
    if (prevMobileRef.current && !isMobile) return;
    if (!prevMobileRef.current && isMobile) {
      setCardSearchInput(
        (filterModel.quickFilterValues || []).join(" ").trim()
      );
    }
    prevMobileRef.current = isMobile;
  }, [isMobile, filterModel.quickFilterValues]);

  useEffect(() => {
    if (!isMobile) return;
    const t = setTimeout(() => {
      const tokens = cardSearchInput.trim().split(/\s+/).filter(Boolean);
      setFilterModel((fm) => {
        const prev = (fm.quickFilterValues || []).join(" ");
        const next = tokens.join(" ");
        if (prev === next) return fm;
        return { ...fm, quickFilterValues: tokens };
      });
      setPaginationModel((p) => ({ ...p, page: 0 }));
    }, 300);
    return () => clearTimeout(t);
  }, [cardSearchInput, isMobile]);

  const gridSortField = useMemo(() => {
    const f = sortModel?.[0]?.field ?? "date";
    const allowed = new Set([
      "id",
      "date",
      "treatment_id",
      "medicine",
      "animal_id",
      "treatment_of",
    ]);
    return allowed.has(f) ? f : "date";
  }, [sortModel]);

  const gridSortDir = sortModel?.[0]?.sort ?? "desc";

  const sortControlValue = `${gridSortField}-${gridSortDir}`;

  const handleCardSortChange = (e) => {
    const v = e.target.value;
    const [field, dir] = v.split("-");
    setSortModel([{ field, sort: dir }]);
    setPaginationModel((p) => ({ ...p, page: 0 }));
  };

  const buildSearch = useCallback(() => {
    const qfTokens = (filterModel.quickFilterValues || [])
      .map((t) => String(t).trim())
      .filter(Boolean);
    return qfTokens.length ? qfTokens.join(" ") : undefined;
  }, [filterModel]);

  const fetchRows = useCallback(async () => {
    if (isSuperAdmin && farmTenantParam == null) {
      setRows([]);
      setRowCount(0);
      setLoading(false);
      setError("");
      return;
    }
    if (!isSuperAdmin && user?.tenant_id == null) {
      setRows([]);
      setRowCount(0);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError("");
      const search = buildSearch();
      const data = await getTreatments(
        {
          page: paginationModel.page + 1,
          pageSize: paginationModel.pageSize,
          search,
          sortBy: gridSortField,
          sortDir: gridSortDir,
          treatmentScope: scopeFilter === "all" ? undefined : scopeFilter,
        },
        isSuperAdmin ? farmTenantParam : null
      );
      setRows(data.items || []);
      setRowCount(data.total ?? 0);
    } catch (err) {
      setError(err?.message || err?.detail || "Failed to fetch treatments");
      setRows([]);
      setRowCount(0);
    } finally {
      setLoading(false);
    }
  }, [
    isSuperAdmin,
    farmTenantParam,
    user?.tenant_id,
    paginationModel,
    gridSortField,
    gridSortDir,
    buildSearch,
    scopeFilter,
  ]);

  useEffect(() => {
    const t = setTimeout(fetchRows, 250);
    return () => clearTimeout(t);
  }, [fetchRows]);

  const handleDelete = useCallback(
    async (row) => {
      if (
        !window.confirm(
          `Delete treatment record ${row.treatment_id}? This cannot be undone.`
        )
      ) {
        return;
      }
      try {
        await deleteTreatment(row.id, isSuperAdmin ? farmTenantParam : null);
        setSnack({
          open: true,
          severity: "success",
          message: "Treatment deleted",
        });
        fetchRows();
      } catch (err) {
        const msg = err?.message || err?.detail || "Delete failed";
        setSnack({ open: true, severity: "error", message: String(msg) });
      }
    },
    [isSuperAdmin, farmTenantParam, fetchRows]
  );

  const columns = useMemo(
    () => [
      { field: "id", headerName: "ID", width: 80, sortable: true },
      {
        field: "treatment_id",
        headerName: "Treatment ID",
        flex: 1,
        minWidth: 140,
        sortable: true,
      },
      {
        field: "subject",
        headerName: "Subject",
        width: 180,
        sortable: false,
        valueGetter: (_, row) =>
          row.treatment_scope === "group"
            ? `Group: ${row.group_name || "unnamed"}`
            : row.animal_tag_number || row.animal_id || "—",
      },
      {
        field: "treatment_of",
        headerName: "Condition",
        flex: 1,
        minWidth: 160,
        sortable: true,
      },
      {
        field: "medicine",
        headerName: "Medicine",
        flex: 1,
        minWidth: 160,
        sortable: true,
      },
      {
        field: "treatment_scope",
        headerName: "Scope",
        width: 120,
        sortable: false,
      },
      {
        field: "group_name",
        headerName: "Group",
        minWidth: 150,
        flex: 1,
        sortable: false,
      },
      {
        field: "group_size",
        headerName: "Group size",
        width: 120,
        sortable: false,
      },
      {
        field: "date",
        headerName: "Date",
        width: 140,
        sortable: true,
        renderCell: (p) => fmtDate(p?.row?.date),
      },
      {
        field: "dispensed_unit_cost",
        headerName: "Unit cost",
        width: 120,
        sortable: false,
        valueGetter: (_, row) =>
          row.dispensed_unit_cost == null
            ? "—"
            : formatUgx(row.dispensed_unit_cost, 4),
      },
      {
        field: "dispensed_total_cost",
        headerName: "Total cost",
        width: 130,
        sortable: false,
        valueGetter: (_, row) =>
          row.dispensed_total_cost == null
            ? "—"
            : formatUgx(row.dispensed_total_cost, 2),
      },
      {
        field: "dispensed_currency",
        headerName: "Currency",
        width: 100,
        sortable: false,
        valueGetter: (_, row) => row.dispensed_currency || "—",
      },
      {
        field: "actions",
        headerName: "Actions",
        sortable: false,
        filterable: false,
        width: 180,
        renderCell: (params) => (
          <>
            <Button
              size="small"
              variant="outlined"
              startIcon={<Visibility />}
              component={RouterLink}
              to={`/treatment/${params.row.id}${profileQuery}`}
            >
              View
            </Button>{" "}
            <IconButton
              size="small"
              color="primary"
              component={RouterLink}
              to={`/treatment/${params.row.id}/edit${profileQuery}`}
              aria-label="edit-treatment"
            >
              <EditOutlined fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              color="error"
              onClick={() => handleDelete(params.row)}
              aria-label="delete-treatment"
            >
              <DeleteOutlined fontSize="small" />
            </IconButton>
          </>
        ),
      },
    ],
    [profileQuery, handleDelete]
  );

  const responsiveColumnOverlay = useMemo(() => {
    if (isXs) {
      return {
        treatment_of: false,
        medicine: false,
        treatment_scope: false,
        group_name: false,
        group_size: false,
        dispensed_unit_cost: false,
        dispensed_total_cost: false,
        dispensed_currency: false,
      };
    }
    if (isSm) {
      return {
        treatment_of: true,
        medicine: true,
        treatment_scope: true,
        group_name: false,
        group_size: false,
        dispensed_unit_cost: false,
        dispensed_total_cost: false,
        dispensed_currency: false,
      };
    }
    return {};
  }, [isXs, isSm]);

  const columnVisibilityModel = useMemo(
    () => ({ ...userColumnVisibility, ...responsiveColumnOverlay }),
    [userColumnVisibility, responsiveColumnOverlay]
  );

  const cardToolbar = (
    <Stack
      direction={{ xs: "column", sm: "row" }}
      spacing={1.5}
      alignItems={{ xs: "stretch", sm: "center" }}
      flexWrap="wrap"
      sx={{ mb: 2 }}
      useFlexGap
    >
      <TextField
        size="small"
        placeholder="Search treatment ID, condition, medicine…"
        value={cardSearchInput}
        onChange={(e) => setCardSearchInput(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" color="action" />
            </InputAdornment>
          ),
        }}
        sx={{ flex: 1, minWidth: { xs: "100%", sm: 200 } }}
      />

      <FormControl size="small" sx={{ minWidth: 160 }}>
        <InputLabel id="treatment-card-sort-label">Sort</InputLabel>
        <Select
          labelId="treatment-card-sort-label"
          label="Sort"
          value={sortControlValue}
          onChange={handleCardSortChange}
        >
          <MenuItem value="date-desc">Date (newest)</MenuItem>
          <MenuItem value="date-asc">Date (oldest)</MenuItem>
          <MenuItem value="treatment_id-asc">Treatment ID (A–Z)</MenuItem>
          <MenuItem value="treatment_id-desc">Treatment ID (Z–A)</MenuItem>
          <MenuItem value="medicine-asc">Medicine (A–Z)</MenuItem>
          <MenuItem value="medicine-desc">Medicine (Z–A)</MenuItem>
          <MenuItem value="treatment_of-asc">Condition (A–Z)</MenuItem>
          <MenuItem value="treatment_of-desc">Condition (Z–A)</MenuItem>
          <MenuItem value="id-desc">ID (newest)</MenuItem>
          <MenuItem value="id-asc">ID (oldest)</MenuItem>
        </Select>
      </FormControl>
    </Stack>
  );

  if (isSuperAdmin && tenantFilter == null) {
    return (
      <Box sx={{ p: 3 }}>
        <Breadcrumbs sx={{ mb: 2, flexWrap: "wrap" }}>
          <Link component={RouterLink} underline="hover" color="inherit" to={APP_HOME_PATH}>
            Dashboard
          </Link>
          <Typography color="text.primary">Treatments</Typography>
        </Breadcrumbs>
        <Typography variant="h4" gutterBottom>
          Treatment records
        </Typography>
        <Alert severity="info" sx={{ mb: 2 }}>
          Select an organization to load treatments for that farm.
        </Alert>
        <Box sx={{ maxWidth: 480 }}>
          <Autocomplete
            options={tenants}
            getOptionLabel={(o) => o?.name ?? ""}
            value={tenantFilter}
            onChange={onOrgChange}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Organization"
                placeholder="Select organization"
              />
            )}
            isOptionEqualToValue={(a, b) => a?.id === b?.id}
          />
        </Box>
      </Box>
    );
  }

  if (!isSuperAdmin && user?.tenant_id == null) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">
          Treatments are available once you belong to an organization.
        </Alert>
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ p: 3 }}>
        <Breadcrumbs sx={{ mb: 2, flexWrap: "wrap" }}>
          <Link component={RouterLink} underline="hover" color="inherit" to={APP_HOME_PATH}>
            Dashboard
          </Link>
          <Typography color="text.primary">Treatments</Typography>
        </Breadcrumbs>

        <Typography variant="h4" sx={{ mb: 2 }}>
          Treatment records
        </Typography>

        {isSuperAdmin && (
          <Box sx={{ maxWidth: 480, my: 2 }}>
            <Autocomplete
              options={tenants}
              getOptionLabel={(o) => o?.name ?? ""}
              value={tenantFilter}
              onChange={onOrgChange}
              renderInput={(params) => (
                <TextField {...params} label="Organization" />
              )}
              isOptionEqualToValue={(a, b) => a?.id === b?.id}
            />
          </Box>
        )}

        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          alignItems={{ xs: "stretch", sm: "center" }}
          justifyContent="space-between"
          sx={{ mb: 2 }}
        >
          <ToggleButtonGroup
            size="small"
            value={scopeFilter}
            exclusive
            onChange={(_, value) => {
              if (!value) return;
              setScopeFilter(value);
              setPaginationModel((p) => ({ ...p, page: 0 }));
            }}
            aria-label="treatment-scope-filter"
          >
            <ToggleButton value="all">All treatments</ToggleButton>
            <ToggleButton value="individual">Individual</ToggleButton>
            <ToggleButton value="group">Group treatments</ToggleButton>
          </ToggleButtonGroup>
          <Button
            variant="contained"
            color="success"
            component={RouterLink}
            to={
              farmTenantParam != null
                ? `/treatment/create_treatment?tenant_id=${farmTenantParam}`
                : "/treatment/create_treatment"
            }
          >
            Add treatment
          </Button>
        </Stack>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {isMobile ? (
          <Paper elevation={1} sx={{ width: "100%", p: 2 }}>
            {cardToolbar}
            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress size={32} />
              </Box>
            ) : (
              <Stack spacing={1.5}>
                {rows.map((row) => (
                  <TreatmentCard
                    key={row.id}
                    row={row}
                    profileQuery={profileQuery}
                    onDelete={handleDelete}
                  />
                ))}
              </Stack>
            )}
            {!loading && rows.length === 0 && (
              <Typography color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
                No treatments match your search.
              </Typography>
            )}
            <TablePagination
              component="div"
              count={rowCount}
              page={paginationModel.page}
              onPageChange={(_, page) =>
                setPaginationModel((m) => ({ ...m, page }))
              }
              rowsPerPage={paginationModel.pageSize}
              rowsPerPageOptions={[5, 10, 25, 50, 100]}
              onRowsPerPageChange={(e) => {
                setPaginationModel({
                  page: 0,
                  pageSize: parseInt(e.target.value, 10),
                });
              }}
            />
          </Paper>
        ) : (
          <Box sx={{ width: "100%", overflowX: "auto" }}>
            <Paper
              elevation={1}
              sx={{ width: "100%", minWidth: isXs ? 720 : "auto" }}
            >
              <DataGrid
                rows={rows}
                columns={columns}
                getRowId={(row) => row.id}
                loading={loading}
                density={isSm ? "compact" : "standard"}
                rowHeight={56}
                showToolbar
                showQuickFilter
                slotProps={{
                  toolbar: {
                    csvOptions: { fileName: "treatments", allColumns: true },
                    printOptions: { disableToolbarButton: true },
                  },
                }}
                pagination
                paginationMode="server"
                rowCount={rowCount}
                paginationModel={paginationModel}
                onPaginationModelChange={setPaginationModel}
                pageSizeOptions={[5, 10, 25, 50, 100]}
                sortingMode="server"
                sortModel={sortModel}
                onSortModelChange={(model) => {
                  setPaginationModel((p) => ({ ...p, page: 0 }));
                  setSortModel(
                    model.length ? model : [{ field: "date", sort: "desc" }]
                  );
                }}
                filterMode="server"
                filterModel={filterModel}
                onFilterModelChange={(model) => {
                  setPaginationModel((p) => ({ ...p, page: 0 }));
                  setFilterModel(model);
                }}
                columnVisibilityModel={columnVisibilityModel}
                onColumnVisibilityModelChange={(model) =>
                  setUserColumnVisibility(model)
                }
                sx={{
                  border: "none",
                  "& .MuiDataGrid-columnHeaders": {
                    backgroundColor: "background.default",
                  },
                }}
                disableRowSelectionOnClick
              />
            </Paper>
          </Box>
        )}
      </Box>

      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={closeSnack}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={closeSnack}
          severity={snack.severity}
          variant="filled"
          elevation={3}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </>
  );
}
